/**
 * The SPA side of seat purchase (Task #1831, US #2570): `GET /api/billing/plans`,
 * `POST /api/billing/checkout`, `GET /api/billing/subscription`, `GET /api/billing/invoices`, and the fake provider's
 * checkout endpoints (`/api/billing/fake/sessions/*`, which exist only for the fake provider and
 * answer 404 anywhere else). Later Tasks add the seat changes (#1838, `/api/billing/seats/*`) and
 * cancelling the subscription (#1839, `POST /api/billing/subscription/cancel` and `/keep`) and
 * switching the billing frequency (#1832, `/api/billing/subscription/frequency/*`).
 *
 * Stateless on purpose: each page owns the loading and error state it shows. Every call returns a
 * result; none throws. The organisation is never sent: the BFF takes it from the caller's session.
 *
 * Nothing here reads `VITE_PUBLIC_REGISTRATION_ENABLED`: the routes and the Settings card gate
 * every call behind their own switch check, so with the switch off this module is never called.
 *
 * @module composables/useSeatBilling
 */

import { useAuthStore } from '@/stores/auth.store'
import type {
  FakeCheckoutSession,
  FakePaymentMethodSession,
  FrequencySwitchPreview,
  FrequencySwitchTerms,
  PendingSeatDecrease,
  PendingSubscriptionChanges,
  SeatChangeOutcome,
  SeatChangePreview,
  SeatInvoicesResponse,
  SeatPlanCode,
  SeatPlansResponse,
  SeatSubscriptionResponse,
  SubscriptionCancelOutcome,
} from '@/types/seat-billing.types'

export type SeatBillingResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string; status: number | null; reason?: string }

function baseUrl(): string {
  // VITE_BFF_URL already includes /api (the same base useProfile uses).
  return `${import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'}/billing`
}

/** The failure's message, and the machine-readable `reason` a 409 carries (checkout refused). */
async function failureFrom(
  response: Response,
  fallback: string
): Promise<{ error: string; reason?: string }> {
  if (response.status === 429) {
    return { error: 'Too many requests in a short time. Wait a minute, then try again.' }
  }
  const body: unknown = await response.json().catch(() => null)
  const fields = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {}
  const message = fields['error']
  const reason = fields['reason']
  return {
    error: typeof message === 'string' && message.length > 0 ? message : fallback,
    ...(typeof reason === 'string' ? { reason } : {}),
  }
}

export interface UseSeatBillingReturn {
  loadPlans: () => Promise<SeatBillingResult<SeatPlansResponse>>
  loadSubscription: () => Promise<SeatBillingResult<SeatSubscriptionResponse>>
  /** The organisation's seat-subscription invoices (billing history), newest first. */
  loadInvoices: (after?: string) => Promise<SeatBillingResult<SeatInvoicesResponse>>
  /** Starts a checkout; resolves the URL to send the browser to. */
  startCheckout: (
    planCode: SeatPlanCode,
    quantity: number
  ) => Promise<SeatBillingResult<{ url: string }>>
  /**
   * Task #1835: the provider's page for the organisation's payment methods; resolves its URL. It
   * returns to the subscription page, or (Task #1838) to the seats page for `'seats'`: a KEY the
   * server allow-lists, never a URL.
   */
  openPaymentMethodPortal: (returnTo?: 'seats') => Promise<SeatBillingResult<{ url: string }>>
  loadFakePaymentMethodSession: (
    sessionId: string
  ) => Promise<SeatBillingResult<FakePaymentMethodSession>>
  replaceFakeCard: (sessionId: string) => Promise<SeatBillingResult<{ redirectUrl: string }>>
  cancelFakePaymentMethod: (
    sessionId: string
  ) => Promise<SeatBillingResult<{ redirectUrl: string }>>
  /** Task #1838: the fake page's "Use a card that declines". */
  useDecliningFakeCard: (sessionId: string) => Promise<SeatBillingResult<{ redirectUrl: string }>>
  /** Task #1838: what changing to `quantity` seats would do, priced now. */
  previewSeatChange: (quantity: number) => Promise<SeatBillingResult<SeatChangePreview>>
  /**
   * Task #1838: confirm a previewed change. Its proration date, amount (an increase), effective
   * date (a decrease) and pending decrease go back unchanged, so the server refuses anything but
   * the change shown.
   */
  confirmSeatChange: (change: {
    quantity: number
    prorationDate: string
    expectedAmountMinor: number | null
    expectedEffectiveAt: string | null
    expectedPendingDecrease: PendingSeatDecrease | null
  }) => Promise<SeatBillingResult<{ outcome: SeatChangeOutcome }>>
  cancelSeatDecrease: () => Promise<SeatBillingResult<{ cancelled: boolean }>>
  /** Tasks #1838, #1839: the pending seat decrease and any scheduled cancel, read live. */
  loadPendingChanges: () => Promise<SeatBillingResult<PendingSubscriptionChanges>>
  /**
   * Task #1839: cancel the subscription at the end of its period (or trial), or now (no refund of
   * the unused time). A period-end cancel names the date the admin confirmed (ISO): the server
   * refuses it (`effective_date_changed`) when the period now ends on another day.
   */
  cancelSubscription: (
    request: { when: 'period_end'; expectedCancelAt: string } | { when: 'now' }
  ) => Promise<SeatBillingResult<{ outcome: SubscriptionCancelOutcome }>>
  /** Task #1839: undo a cancel scheduled for the period end. */
  keepSubscription: () => Promise<SeatBillingResult<{ outcome: SubscriptionCancelOutcome }>>
  /** Task #1832: what switching to `interval` at the next renewal would bill. */
  previewFrequencySwitch: (
    interval: 'month' | 'year'
  ) => Promise<SeatBillingResult<FrequencySwitchPreview>>
  /**
   * Task #1832: confirm a previewed switch; the preview's date, seats, unit price and effective
   * date go back unchanged, so the server refuses anything but the switch shown.
   */
  confirmFrequencySwitch: (confirmation: {
    interval: 'month' | 'year'
    previewedAt: string
    expectedQuantity: number
    expectedUnitAmountMinor: number
    expectedEffectiveAt: string
  }) => Promise<SeatBillingResult<{ outcome: { kind: 'switch_scheduled' } & FrequencySwitchTerms }>>
  /** Task #1832: drop a pending switch (a pending seat decrease stays). */
  cancelFrequencySwitch: () => Promise<SeatBillingResult<{ cancelled: boolean }>>
  loadFakeSession: (sessionId: string) => Promise<SeatBillingResult<FakeCheckoutSession>>
  payFakeSession: (sessionId: string) => Promise<SeatBillingResult<{ redirectUrl: string }>>
  cancelFakeSession: (sessionId: string) => Promise<SeatBillingResult<{ redirectUrl: string }>>
}

export function useSeatBilling(): UseSeatBillingReturn {
  const authStore = useAuthStore()

  async function send<T>(
    path: string,
    init: { method: 'GET' } | { method: 'POST'; body?: string },
    fallback: string
  ): Promise<SeatBillingResult<T>> {
    const token = authStore.isAuthenticated ? await authStore.getAccessToken() : null
    if (!token?.ok) return { ok: false, error: 'You are not signed in.', status: null }
    try {
      const response = await fetch(`${baseUrl()}${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.value}` },
      })
      if (!response.ok) {
        return { ok: false, ...(await failureFrom(response, fallback)), status: response.status }
      }
      return { ok: true, value: (await response.json()) as T }
    } catch {
      return {
        ok: false,
        error: `${fallback} Check your connection and try again.`,
        status: null,
      }
    }
  }

  return {
    loadPlans: () =>
      send<SeatPlansResponse>('/plans', { method: 'GET' }, "Couldn't load the plans."),
    loadSubscription: () =>
      send<SeatSubscriptionResponse>(
        '/subscription',
        { method: 'GET' },
        "Couldn't load your subscription."
      ),
    loadInvoices: after =>
      send<SeatInvoicesResponse>(
        after ? `/invoices?after=${encodeURIComponent(after)}` : '/invoices',
        { method: 'GET' },
        "Couldn't load your billing history."
      ),
    startCheckout: (planCode, quantity) =>
      send<{ url: string }>(
        '/checkout',
        { method: 'POST', body: JSON.stringify({ planCode, quantity }) },
        "Couldn't start checkout."
      ),
    openPaymentMethodPortal: returnTo =>
      send<{ url: string }>(
        '/payment-method/portal-session',
        { method: 'POST', body: JSON.stringify(returnTo ? { returnTo } : {}) },
        "Couldn't open the payment method page."
      ),
    loadFakePaymentMethodSession: sessionId =>
      send<FakePaymentMethodSession>(
        `/fake/payment-method-sessions/${encodeURIComponent(sessionId)}`,
        { method: 'GET' },
        "Couldn't load this page."
      ),
    replaceFakeCard: sessionId =>
      send<{ redirectUrl: string }>(
        `/fake/payment-method-sessions/${encodeURIComponent(sessionId)}/replace`,
        { method: 'POST' },
        "Couldn't replace the card."
      ),
    cancelFakePaymentMethod: sessionId =>
      send<{ redirectUrl: string }>(
        `/fake/payment-method-sessions/${encodeURIComponent(sessionId)}/cancel`,
        { method: 'POST' },
        "Couldn't go back."
      ),
    useDecliningFakeCard: sessionId =>
      send<{ redirectUrl: string }>(
        `/fake/payment-method-sessions/${encodeURIComponent(sessionId)}/decline-card`,
        { method: 'POST' },
        "Couldn't change the card."
      ),
    previewSeatChange: quantity =>
      send<SeatChangePreview>(
        `/seats/preview?quantity=${encodeURIComponent(String(quantity))}`,
        { method: 'GET' },
        "Couldn't price the change."
      ),
    confirmSeatChange: change =>
      send<{ outcome: SeatChangeOutcome }>(
        '/seats/change',
        { method: 'POST', body: JSON.stringify(change) },
        "Couldn't change the seats."
      ),
    cancelSeatDecrease: () =>
      send<{ cancelled: boolean }>(
        '/seats/cancel-decrease',
        { method: 'POST', body: JSON.stringify({}) },
        "Couldn't cancel the seat change."
      ),
    loadPendingChanges: () =>
      send<PendingSubscriptionChanges>(
        '/seats/pending',
        { method: 'GET' },
        "Couldn't load the pending seat change."
      ),
    cancelSubscription: request =>
      send<{ outcome: SubscriptionCancelOutcome }>(
        '/subscription/cancel',
        { method: 'POST', body: JSON.stringify(request) },
        "Couldn't cancel the subscription."
      ),
    keepSubscription: () =>
      send<{ outcome: SubscriptionCancelOutcome }>(
        '/subscription/keep',
        { method: 'POST', body: JSON.stringify({}) },
        "Couldn't keep the subscription."
      ),
    previewFrequencySwitch: interval =>
      send<FrequencySwitchPreview>(
        `/subscription/frequency/preview?interval=${encodeURIComponent(interval)}`,
        { method: 'GET' },
        "Couldn't price the switch."
      ),
    confirmFrequencySwitch: confirmation =>
      send<{ outcome: { kind: 'switch_scheduled' } & FrequencySwitchTerms }>(
        '/subscription/frequency/switch',
        { method: 'POST', body: JSON.stringify(confirmation) },
        "Couldn't switch the billing frequency."
      ),
    cancelFrequencySwitch: () =>
      send<{ cancelled: boolean }>(
        '/subscription/frequency/cancel',
        { method: 'POST', body: JSON.stringify({}) },
        "Couldn't cancel the billing frequency switch."
      ),
    loadFakeSession: sessionId =>
      send<FakeCheckoutSession>(
        `/fake/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'GET' },
        "Couldn't load this checkout."
      ),
    payFakeSession: sessionId =>
      send<{ redirectUrl: string }>(
        `/fake/sessions/${encodeURIComponent(sessionId)}/pay`,
        { method: 'POST' },
        "Couldn't complete the payment."
      ),
    cancelFakeSession: sessionId =>
      send<{ redirectUrl: string }>(
        `/fake/sessions/${encodeURIComponent(sessionId)}/cancel`,
        { method: 'POST' },
        "Couldn't cancel the checkout."
      ),
  }
}
